import { useMutation, useLazyQuery } from '@apollo/client';
import { Pda, PdaScanEvent } from './components/Pda';
import { Header } from './components/Header';
import { useRef, useState, useCallback } from 'react';
import { Column, createRecord, insertRecordBySeq, ResultTable, ScanRecord } from './components/ResultTable';
import { useScanQueueConsumer } from './components/useScanQueueConsumer';
import { ScanInput } from './components/ScanInput';
import { isValidBarCode } from 'utils/BarCodeUtil';
import { parseException, showError, parseExceptionMessageKey, getTranslatedMessage } from 'utils/exceptionUtils';
import { playScanSound, playScanFailedAoSound } from 'utils/scanUtils';
import { t } from '../../i18n';
import { formatLocalizedTime } from '../../utils/dateFormatUtils';
import {
    CHECK_LOCATION_EXISTS,
    CheckLocationExistsResponse,
    CheckLocationExistsVariables
} from '../../graphql/queries/warehouseQueries';
import {
    HANDLE_PUTAWAY_SCAN_EVENT,
    HandlePutawayScanEventResponse,
    HandlePutawayScanEventVariables
} from '../../graphql/mutations/putawayScan';
import { Dialog } from 'antd-mobile';
import { LocationOrderScanOverlay, LocationOrderScanData } from './components/LocationOrderScanOverlay';

// 简单的键盘关闭函数
const closeKeyboard = () => {
    // 移除当前焦点
    if (document.activeElement && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
    }
    
    // 失焦所有输入框
    const inputs = document.querySelectorAll('input, textarea');
    inputs.forEach(input => {
        if (input instanceof HTMLElement) {
            input.blur();
        }
    });
};

const PageBody = () => {
    const [successRecords, setSuccessRecords] = useState<ScanRecord[]>([]);
    const [failedRecords, setFailedRecords] = useState<ScanRecord[]>([]);
    const [locationNo, setLocationNo] = useState('');
    const [trackingNumber, setTrackingNumber] = useState('');
    const [locationNoInput, setLocationNoInput] = useState('');
    const [trackingNumberInput, setTrackingNumberInput] = useState('');
    const [nextSequenceNo, setNextSequenceNo] = useState(1);
    const [confirmPutaway, setConfirmPutaway] = useState(false);
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [overlayData, setOverlayData] = useState<LocationOrderScanData | null>(null);

    const locationNoInputRef = useRef<HTMLInputElement>(null);
    const trackingNumberRef = useRef<HTMLInputElement>(null);

    const [checkLocationExists] = useLazyQuery<CheckLocationExistsResponse, CheckLocationExistsVariables>(
        CHECK_LOCATION_EXISTS
    );

    const [handlePutawayScanEvent] = useMutation<HandlePutawayScanEventResponse, HandlePutawayScanEventVariables>(
        HANDLE_PUTAWAY_SCAN_EVENT
    );

    // refs for state read inside onProcess
    const locationNoRef = useRef(locationNo);
    locationNoRef.current = locationNo;
    const overlayVisibleRef = useRef(overlayVisible);
    overlayVisibleRef.current = overlayVisible;
    const nextSequenceNoRef = useRef(nextSequenceNo);
    nextSequenceNoRef.current = nextSequenceNo;
    const confirmPutawayRef = useRef(confirmPutaway);
    confirmPutawayRef.current = confirmPutaway;

    // 错误原因映射
    const getPutawayReasonMap = (): Record<string, string> => ({
        'errors.business.putaway.orderAlreadyDelivered': t('pda.pages.putaway.packageDelivered'),
        'errors.business.putaway.orderAlreadyReturned': t('pda.pages.putaway.packageReturned'),
        'errors.business.putaway.orderInDelivery': t('pda.pages.putaway.packageInDelivery'),
        'errors.business.putaway.orderDriverPicked': t('pda.pages.putaway.driverPicked'),
        'errors.business.putaway.orderMissingReturnScan': t('pda.pages.putaway.missingReturnScan'),
        'errors.business.putaway.nonExceptionOrderNotAllowed': t('pda.pages.putaway.nonExceptionOrderNotAllowed'),
        'errors.business.putaway.lostNotAllowed': t('pda.pages.putaway.lostNotAllowed'),
        'errors.business.putaway.damagedNotAllowed': t('pda.pages.putaway.damagedNotAllowed'),
        'errors.business.putaway.invalidExceptionReasonOther': t('pda.pages.putaway.otherExceptionReason'),
        'errors.business.putaway.packageAlreadyPutaway': t('pda.pages.putaway.packageAlreadyPutaway'),
        'errors.business.putaway.locationFull': t('pda.pages.putaway.locationFull')
    });

    const onProcess = useCallback(async (event: PdaScanEvent) => {
        const code = event.code;

        // 进行下一个扫描时，关闭上一个弹窗
        if (overlayVisibleRef.current) {
            setOverlayVisible(false);
            setOverlayData(null);
        }

        // 两阶段扫描：先检查库位号是否存在
        if (!locationNoRef.current) {
            // 库位号不存在，将此次扫描作为库位扫描
            try {
                const { data } = await checkLocationExists({
                    variables: { locationNo: code }
                });
                const result = data?.checkLocationExists;
                if (result?.success) {
                    setLocationNo(code);
                    setLocationNoInput(code);
                    playScanSound();
                } else {
                    playScanFailedAoSound();
                    setLocationNoInput('');
                    const messageKey = result?.messageKey || 'errors.business.location.notSelected';
                    const message = getTranslatedMessage(messageKey, t('pda.pages.putaway.pleaseSelectLocation'));
                    showError({
                        title: t('pda.common.error'),
                        message: message || t('pda.pages.putaway.pleaseSelectLocation')
                    });
                }
            } catch (error: any) {
                playScanFailedAoSound();
                const { title, message } = parseException(error);
                showError({ title, message });

                setFailedRecords(prev => insertRecordBySeq(prev, createRecord({
                    value1: code,
                    failedReason: message,
                    time: formatLocalizedTime(),
                    seq: event.seq
                })));

                setLocationNoInput('');
            }
            return;
        }

        // 库位号已存在，将此次扫描作为包裹号扫描
        setTrackingNumberInput(code);
        const currentLocationNo = locationNoRef.current;

        try {
            const { data } = await handlePutawayScanEvent({
                variables: {
                    trackingNumber: code,
                    locationNo: currentLocationNo,
                    confirmPutaway: confirmPutawayRef.current
                }
            });
            const result = data?.handlePutawayScanEvent;
            if (result?.success) {
                playScanSound();
                const sequenceNo = String(nextSequenceNoRef.current).padStart(4, '0');
                setSuccessRecords(prev => insertRecordBySeq(prev, createRecord({
                    value2: code,
                    time: formatLocalizedTime(),
                    value1: sequenceNo,
                    seq: event.seq
                })));
                setNextSequenceNo(prev => prev + 1);
                setConfirmPutaway(false);
                setTrackingNumberInput('');

                if (result.packageInfo) {
                    closeKeyboard();
                    setOverlayData({
                        locationNo: result.packageInfo.locationNo || currentLocationNo,
                        trackingNumber: result.packageInfo.trackingNumber || code,
                        locationOrderNo: result.packageInfo.locationOrderNo || undefined
                    });
                    setOverlayVisible(true);
                }
            } else {
                playScanFailedAoSound();
                const messageKey = result?.messageKey;

                // 处理需要确认的情况
                if (result?.requireConfirmation && messageKey) {
                    const message = getTranslatedMessage(messageKey, t('pda.common.scanFailed'));
                    const confirmed = await new Promise<boolean>((resolve) => {
                        Dialog.confirm({
                            content: message,
                            confirmText: t('pda.common.confirm'),
                            cancelText: t('pda.common.cancel'),
                            onConfirm: () => resolve(true),
                            onCancel: () => resolve(false)
                        });
                    });

                    if (confirmed) {
                        setConfirmPutaway(true);
                        try {
                            const { data: confirmData } = await handlePutawayScanEvent({
                                variables: {
                                    trackingNumber: code,
                                    locationNo: currentLocationNo,
                                    confirmPutaway: true
                                }
                            });
                            const confirmResult = confirmData?.handlePutawayScanEvent;
                            if (confirmResult?.success) {
                                playScanSound();
                                const sequenceNo = String(nextSequenceNoRef.current).padStart(4, '0');
                                setSuccessRecords(prev => insertRecordBySeq(prev, createRecord({
                                    value2: code,
                                    time: formatLocalizedTime(),
                                    value1: sequenceNo,
                                    seq: event.seq
                                })));
                                setNextSequenceNo(prev => prev + 1);
                                setConfirmPutaway(false);
                                setTrackingNumberInput('');

                                if (confirmResult.packageInfo) {
                                    closeKeyboard();
                                    setOverlayData({
                                        locationNo: confirmResult.packageInfo.locationNo || currentLocationNo,
                                        trackingNumber: confirmResult.packageInfo.trackingNumber || code,
                                        locationOrderNo: confirmResult.packageInfo.locationOrderNo || undefined
                                    });
                                    setOverlayVisible(true);
                                }
                            } else {
                                playScanFailedAoSound();
                                const confirmMessageKey = confirmResult?.messageKey;
                                let errorMessage = confirmMessageKey ? getTranslatedMessage(confirmMessageKey, t('pda.common.scanFailed')) : t('pda.common.scanFailed');
                                let failedReason = errorMessage;
                                const reasonMap = getPutawayReasonMap();
                                if (confirmMessageKey) {
                                    failedReason = reasonMap[confirmMessageKey] || errorMessage;
                                }
                                const toastMessage = confirmMessageKey && confirmMessageKey === 'errors.business.putaway.orderWrongStatus'
                                    ? t('pda.pages.putaway.scanFailedCheckStatus')
                                    : errorMessage;
                                showError({ title: t('pda.common.scanFailed'), message: toastMessage });
                                setFailedRecords(prev => insertRecordBySeq(prev, createRecord({
                                    value2: code,
                                    failedReason: failedReason,
                                    time: formatLocalizedTime(),
                                    seq: event.seq
                                })));
                                setTrackingNumberInput('');
                            }
                        } catch (confirmError: any) {
                            playScanFailedAoSound();
                            const { title, message: errMsg } = parseException(confirmError);
                            showError({ title, message: errMsg });
                            setFailedRecords(prev => insertRecordBySeq(prev, createRecord({
                                value2: code,
                                failedReason: errMsg,
                                time: formatLocalizedTime(),
                                seq: event.seq
                            })));
                            setTrackingNumberInput('');
                        }
                    } else {
                        setConfirmPutaway(false);
                        setTrackingNumberInput('');
                    }
                    return;
                }

                // 处理其他错误
                let errorMessage = messageKey ? getTranslatedMessage(messageKey, t('pda.common.scanFailed')) : t('pda.common.scanFailed');
                let failedReason = errorMessage;
                const reasonMap = getPutawayReasonMap();
                if (messageKey) {
                    failedReason = reasonMap[messageKey] || errorMessage;
                }
                const toastMessage = messageKey && messageKey === 'errors.business.putaway.orderWrongStatus'
                    ? t('pda.pages.putaway.scanFailedCheckStatus')
                    : errorMessage;
                showError({ title: t('pda.common.scanFailed'), message: toastMessage });
                setFailedRecords(prev => insertRecordBySeq(prev, createRecord({
                    value2: code,
                    failedReason: failedReason,
                    time: formatLocalizedTime(),
                    seq: event.seq
                })));
                setTrackingNumberInput('');
            }
        } catch (error: any) {
            playScanFailedAoSound();
            const messageKey = parseExceptionMessageKey(error);
            const { title, message } = parseException(error);
            let failedReason = message;
            const reasonMap = getPutawayReasonMap();
            if (messageKey) {
                failedReason = reasonMap[messageKey] || message;
            }
            showError({ title, message });
            setFailedRecords(prev => insertRecordBySeq(prev, createRecord({
                value2: code,
                failedReason: failedReason,
                time: formatLocalizedTime(),
                seq: event.seq
            })));
            setTrackingNumberInput('');
        }
    }, [checkLocationExists, handlePutawayScanEvent]);

    const { enqueueScan } = useScanQueueConsumer({ maxConcurrency: 1, onProcess });

    // 统一的扫描入口
    const handleScan = (code: string) => {
        if (!isValidBarCode(code)) {
            return;
        }
        if (overlayVisible) {
            setOverlayVisible(false);
            setOverlayData(null);
        }
        enqueueScan(code);
    };

    const successColumns: Column[] = [
        { title: t('pda.common.trackingNO'), key: 'value2' },
        { title: t('pda.common.time'), key: 'time' },
        { title: t('pda.common.no'), key: 'value1' }
    ];

    const failedColumns: Column[] = [
        { title: t('pda.common.trackingNO'), key: 'value2' },
        { title: t('pda.common.time'), key: 'time' },
        { title: t('pda.common.failedReason'), key: 'failedReason' }
    ];

    // 格式化成功记录的序号为4位数字
    const formattedSuccessRecords = successRecords.map(record => ({
        ...record,
        value1: record.value1 ? String(record.value1).padStart(4, '0') : record.value1
    }));

    // 当用户手动修改或删除库位号时，同步清空 locationNo 状态
    const handleLocationNoInputChange = (value: string) => {
        setLocationNoInput(value);
        if (value.trim() === '') {
            //用户清空输入框时，清除已确认的库位号，允许重新扫描
            setLocationNo('');
            setTrackingNumberInput('');  // 同时清除包裹号输入
            // 保留历史记录（successRecords 和 failedRecords）
            // 只重置序号，下一个库位的序号从 1 开始
            setNextSequenceNo(1);
        }
        // 其他情况不动 locationNo，等待校验通过后赋值
    };

    // 当用户手动修改或删除包裹号时，同步清空 trackingNumber 状态
    const handleTrackingNumberInputChange = (value: string) => {
        setTrackingNumberInput(value);
        if (value.trim() !== trackingNumber) {
            setTrackingNumber('');
        }
    };

    const handleOverlayClose = () => {
        setOverlayVisible(false);
        setOverlayData(null);
    };

    return (
        <div className="flex flex-col gap-2">
            <ScanInput
                ref={locationNoInputRef}
                placeholder={t('pda.pages.putaway.locationNo')}
                value={locationNoInput}
                onScan={handleScan}
                onChange={handleLocationNoInputChange}
            />
            <ScanInput
                ref={trackingNumberRef}
                placeholder={t('pda.pages.putaway.trackingNO')}
                value={trackingNumberInput}
                onScan={handleScan}
                onChange={handleTrackingNumberInputChange}
            />
            <ResultTable
                successRecords={formattedSuccessRecords}
                failedRecords={failedRecords}
                successColumns={successColumns}
                failedColumns={failedColumns}
            />
            {overlayData && (
                <LocationOrderScanOverlay
                    visible={overlayVisible}
                    data={overlayData}
                    onClose={handleOverlayClose}
                    color="green"
                />
            )}
        </div>
    );
};

export default function PutawayScanPage() {
    return (
        <div className="w-full h-full flex flex-col gap-2 p-2">
            <Header title={t('pda.pages.putaway.putaway')} />
            <Pda />
            <PageBody />
        </div>
    );
}
